import React, { useCallback } from "react";
import { connect } from "react-redux";

import { Pagination } from "antd";
import { useIntl } from "react-intl";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";

import Table from "@app/component/Table";
import { DEFAULT_EXPORT_PAGE_SIZE } from "@app/scene/reports/utils/const";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import PermissionGetter from "@app/types/PermissionGetter";
import Report from "@app/types/Report";

import { fetchExportCardData } from "../../store/actions";

import styles from "./ReportTable.module.scss";
import useReportListColumns from "./useReportListColumns";

export interface ReportTableProps {
  fetchExportCardData: typeof fetchExportCardData.request;
  loading: boolean;
  onClone: (report: Report) => void;
  onDelete: (report: Report) => void;
  onDownload: (report: Report) => void;
  onEdit: (report: Report) => void;
  onSchedule: (report: Report) => void;
  onToggleFavorite: (report: Report) => void;
  page: number;
  reports: Report[];
  total: number;
}

interface Props extends ReportTableProps {
  hasPermission: PermissionGetter;
}

const ReportTable: React.FC<Props> = ({
  fetchExportCardData,
  hasPermission,
  loading,
  onClone,
  onDelete,
  onDownload,
  onEdit,
  onSchedule,
  onToggleFavorite,
  page,
  reports,
  total,
}) => {
  const intl = useIntl();
  const columnConfig = useReportListColumns(
    intl,
    hasPermission(PermissionName.EXPORT_DOWNLOAD) ? onDownload : undefined,
    hasPermission(PermissionName.EXPORT_UPDATE) ? onEdit : undefined,
    hasPermission(PermissionName.EXPORT_DOWNLOAD) ? onSchedule : undefined,
    hasPermission(PermissionName.EXPORT_DELETE) ? onDelete : undefined,
    hasPermission(PermissionName.EXPORT_CREATE) ? onClone : undefined,
    hasPermission(PermissionName.EXPORT_UPDATE) ? onToggleFavorite : undefined
  );

  const handleGoToPage = useCallback(
    (page: number) => fetchExportCardData({ page: page - 1 }),
    [fetchExportCardData]
  );

  return (
    <>
      <Table<Report>
        className={styles.container}
        columns={columnConfig}
        dataSource={reports}
        loading={loading}
        pagination={false}
        rowKey="id"
        scrollable
      />
      <Pagination
        className={styles.pagination}
        current={page + 1}
        defaultPageSize={DEFAULT_EXPORT_PAGE_SIZE}
        onChange={handleGoToPage}
        showSizeChanger={false}
        total={total}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasPermission: hasPermission(state),
});

export default connect(mapStateToProps)(ReportTable);
